
import { QUERY_VERSION } from "@/constants/serviceQueryConstants";
import { QueryClient } from "@tanstack/react-query";

/**
 * Utility to clear all caches related to services and database schema
 */
export const clearServiceCaches = async (queryClient: QueryClient): Promise<boolean> => {
  try {
    console.log('[Cache] Clearing service caches for version', QUERY_VERSION);
    
    // Reset query version in local storage
    localStorage.setItem('lamulamu_query_version', QUERY_VERSION);
    localStorage.setItem('lamulamu_schema_version', QUERY_VERSION);
    
    // Clear all service-related query cache entries
    await queryClient.removeQueries({ queryKey: ['services'] });
    await queryClient.removeQueries({ queryKey: ['service'] });
    
    // Clear any other application cache for services
    localStorage.removeItem('lamulamu_services_cache');
    localStorage.removeItem('lamulamu_last_services');
    
    // Try to clear browser cache API entries for services
    if ('caches' in window) {
      try {
        // Try to delete specific service-related cache entries
        const cache = await caches.open('api-cache');
        const keys = await cache.keys();
        const serviceRequests = keys.filter(key => 
          key.url.includes('/services') || 
          key.url.includes('/service_images')
        );
        
        for (const request of serviceRequests) {
          await cache.delete(request);
        }
        
        console.log(`[Cache] Cleared ${serviceRequests.length} API cache entries`);
      } catch (err) {
        console.warn('[Cache] Error clearing Cache API:', err);
      }
    }
    
    return true;
  } catch (error) {
    console.error('[Cache] Error clearing service caches:', error);
    return false;
  }
};

/**
 * Completely refreshes the application state including cache and reloads the page
 */
export const forceRefreshApplication = (queryClient: QueryClient): void => {
  // Clear caches first
  clearServiceCaches(queryClient)
    .then(() => {
      console.log('[App] Forcing application reload after cache clear');
      
      // Force a hard reload
      window.location.reload();
    })
    .catch(error => {
      console.error('[App] Error during force refresh:', error);
      // Try to reload anyway
      window.location.reload();
    });
};
